import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Filter,
  Users,
  Building2,
  Mail,
  ExternalLink,
  BookOpen,
  Award,
  TrendingUp,
  MapPin,
  Calendar,
  BarChart3,
  Network,
  MessageSquare,
  UserPlus,
  Grid3X3,
  List,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { Researcher } from '@/types/research';
import { cn } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export function ResearchResearchersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [institutionFilter, setInstitutionFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('citationCount');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedResearcher, setSelectedResearcher] = useState<Researcher | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['research-researchers', searchQuery, institutionFilter, sortBy],
    queryFn: () =>
      researchService.listResearchers({
        search: searchQuery || undefined,
        institution: institutionFilter !== 'all' ? institutionFilter : undefined,
        sortBy: sortBy as 'name' | 'citationCount' | 'hIndex' | 'publicationCount',
        sortOrder: 'desc',
      }),
  });

  const { data: institutions } = useQuery({
    queryKey: ['research-institutions-list'],
    queryFn: () => researchService.listInstitutions(),
  });

  const researchers = data?.data || [];

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  };

  // Sample publication data for researcher detail
  const publicationYearData = [
    { year: '2019', publications: 8 },
    { year: '2020', publications: 12 },
    { year: '2021', publications: 15 },
    { year: '2022', publications: 18 },
    { year: '2023', publications: 22 },
    { year: '2024', publications: 12 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Researchers & Experts</h1>
          <p className="text-muted-foreground">
            Discover researchers, explore expertise, and build collaborations
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, expertise, or research interests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={institutionFilter} onValueChange={setInstitutionFilter}>
                <SelectTrigger className="w-[200px]">
                  <Building2 className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Institution" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Institutions</SelectItem>
                  {institutions?.data.map((inst) => (
                    <SelectItem key={inst.id} value={inst.name}>
                      {inst.shortName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="citationCount">Most Cited</SelectItem>
                  <SelectItem value="hIndex">h-Index</SelectItem>
                  <SelectItem value="publicationCount">Publications</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-medium">{researchers.length}</span> researchers
        </p>
      </div>

      {/* Researchers Grid/List */}
      {isLoading ? (
        <div className={cn(
          'grid gap-4',
          viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
        )}>
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-32 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {researchers.map((researcher) => (
            <Card
              key={researcher.id}
              className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50"
              onClick={() => setSelectedResearcher(researcher)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={researcher.profileImageUrl} alt={`${researcher.firstName} ${researcher.lastName}`} />
                    <AvatarFallback className="text-lg bg-gradient-to-br from-primary to-purple-500 text-white">
                      {getInitials(researcher.firstName, researcher.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">
                      {researcher.title} {researcher.firstName} {researcher.lastName}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">{researcher.position}</p>
                    <p className="text-sm text-primary truncate">{researcher.institutionName}</p>
                  </div>
                </div>

                {/* Expertise Tags */}
                <div className="flex flex-wrap gap-1 mt-4">
                  {researcher.expertise.slice(0, 3).map((exp) => (
                    <Badge key={exp} variant="secondary" className="text-xs">
                      {exp}
                    </Badge>
                  ))}
                  {researcher.expertise.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{researcher.expertise.length - 3}
                    </Badge>
                  )}
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-lg font-bold text-primary">{researcher.publicationCount}</p>
                    <p className="text-xs text-muted-foreground">Publications</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-primary">{researcher.citationCount.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Citations</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-primary">{researcher.hIndex}</p>
                    <p className="text-xs text-muted-foreground">h-Index</p>
                  </div>
                </div>

                {/* ORCID */}
                {researcher.orcidId && (
                  <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t">
                    <img
                      src="https://info.orcid.org/wp-content/uploads/2019/11/orcid_16x16.png"
                      alt="ORCID"
                      className="h-4 w-4"
                    />
                    <span className="text-xs text-muted-foreground">{researcher.orcidId}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {researchers.map((researcher) => (
            <Card
              key={researcher.id}
              className="cursor-pointer hover:shadow-md transition-all hover:border-primary/50"
              onClick={() => setSelectedResearcher(researcher)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={researcher.profileImageUrl} alt={`${researcher.firstName} ${researcher.lastName}`} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-purple-500 text-white">
                      {getInitials(researcher.firstName, researcher.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">
                      {researcher.title} {researcher.firstName} {researcher.lastName}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {researcher.position} • {researcher.institutionName}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {researcher.expertise.slice(0, 2).map((exp) => (
                      <Badge key={exp} variant="secondary" className="text-xs hidden md:inline-flex">
                        {exp}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="font-bold">{researcher.publicationCount}</p>
                      <p className="text-xs text-muted-foreground">Pubs</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold">{researcher.citationCount.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Citations</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold">{researcher.hIndex}</p>
                      <p className="text-xs text-muted-foreground">h-Index</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()}>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Contact
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Researcher Detail Dialog */}
      <Dialog open={!!selectedResearcher} onOpenChange={() => setSelectedResearcher(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedResearcher && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-6">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src={selectedResearcher.profileImageUrl} alt={`${selectedResearcher.firstName} ${selectedResearcher.lastName}`} />
                    <AvatarFallback className="text-2xl bg-gradient-to-br from-primary to-purple-500 text-white">
                      {getInitials(selectedResearcher.firstName, selectedResearcher.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <DialogTitle className="text-2xl">
                      {selectedResearcher.title} {selectedResearcher.firstName} {selectedResearcher.lastName}
                    </DialogTitle>
                    <DialogDescription className="mt-1 text-base">
                      {selectedResearcher.position}
                    </DialogDescription>
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-4 w-4" />
                        {selectedResearcher.institutionName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {selectedResearcher.department}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      {selectedResearcher.orcidId && (
                        <Badge variant="outline" className="gap-1">
                          <img
                            src="https://info.orcid.org/wp-content/uploads/2019/11/orcid_16x16.png"
                            alt="ORCID"
                            className="h-3 w-3"
                          />
                          {selectedResearcher.orcidId}
                        </Badge>
                      )}
                      <Badge variant="secondary" className={selectedResearcher.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                        {selectedResearcher.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="publications">Publications</TabsTrigger>
                  <TabsTrigger value="collaborations">Collaborations</TabsTrigger>
                  <TabsTrigger value="metrics">Metrics</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6 mt-4">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <BookOpen className="h-6 w-6 mx-auto text-primary mb-2" />
                        <p className="text-2xl font-bold">{selectedResearcher.publicationCount}</p>
                        <p className="text-xs text-muted-foreground">Publications</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <TrendingUp className="h-6 w-6 mx-auto text-green-500 mb-2" />
                        <p className="text-2xl font-bold">{selectedResearcher.citationCount.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">Citations</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <Award className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                        <p className="text-2xl font-bold">{selectedResearcher.hIndex}</p>
                        <p className="text-xs text-muted-foreground">h-Index</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <BarChart3 className="h-6 w-6 mx-auto text-orange-500 mb-2" />
                        <p className="text-2xl font-bold">{selectedResearcher.i10Index}</p>
                        <p className="text-xs text-muted-foreground">i10-Index</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Expertise & Research Interests */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Expertise</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {selectedResearcher.expertise.map((exp) => (
                            <Badge key={exp} variant="default">
                              {exp}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Research Interests</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {selectedResearcher.researchInterests.map((interest) => (
                            <Badge key={interest} variant="secondary">
                              {interest}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* ANZSRC Classification */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Research Classification (ANZSRC)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedResearcher.anzsrcCodes.map((code) => (
                          <Badge key={code} variant="outline">
                            {code}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Contact Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <a href={`mailto:${selectedResearcher.email}`} className="text-primary hover:underline">
                          {selectedResearcher.email}
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="publications" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Publication Timeline</CardTitle>
                      <CardDescription>Publications per year</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={publicationYearData}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="year" className="text-xs" />
                            <YAxis className="text-xs" />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'hsl(var(--card))',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '8px',
                              }}
                            />
                            <Bar dataKey="publications" fill="#667eea" radius={[4, 4, 0, 0]} activeBar={false}/>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="mt-4 text-center text-muted-foreground">
                    <p>View full publication list on researcher's profile page</p>
                  </div>
                </TabsContent>

                <TabsContent value="collaborations" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Network className="h-5 w-5" />
                        Collaboration Network
                      </CardTitle>
                      <CardDescription>Researchers who frequently collaborate</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedResearcher.collaboratorIds.length > 0 ? (
                          <p className="text-sm text-muted-foreground">
                            {selectedResearcher.collaboratorIds.length} active collaborators
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">No collaboration data available</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="metrics" className="mt-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Citation Impact</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Total Citations</span>
                          <span className="font-bold">{selectedResearcher.citationCount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">h-Index</span>
                          <span className="font-bold">{selectedResearcher.hIndex}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">i10-Index</span>
                          <span className="font-bold">{selectedResearcher.i10Index}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Avg. Citations/Publication</span>
                          <span className="font-bold">
                            {(selectedResearcher.citationCount / selectedResearcher.publicationCount).toFixed(1)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Publication Output</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Total Publications</span>
                          <span className="font-bold">{selectedResearcher.publicationCount}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Collaborators</span>
                          <span className="font-bold">{selectedResearcher.collaboratorIds.length}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Resources in System</span>
                          <span className="font-bold">{selectedResearcher.resourceIds.length}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Last Active</span>
                          <span className="font-bold">
                            {new Date(selectedResearcher.lastActiveAt).toLocaleDateString()}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setSelectedResearcher(null)}>
                  Close
                </Button>
                <Button variant="outline">
                  <Mail className="mr-2 h-4 w-4" />
                  Contact
                </Button>
                <Button>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Request Collaboration
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
